/* eslint-disable */
import { DevTool } from '@hookform/devtools';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { FacilityRating, HealthcareEntityService, PeerRating, PeerRatingsService } from '../api';
import { FacilityRatingsService } from '../api/services/FacilityRatingsService';
import { FormScaleSelectField } from '../app/components/FormScaleSelectField';
import { FormTextareaField } from '../app/components/FormTextareaField';
import { FormSubmitContext } from '../app/contexts/FormSubmitContext';
import { useDocumentTitle } from '../app/hooks/use-document-title';
import { healthcareEntityFacilityQueryKeys } from '../app/query-keys-factories/facilities';
import { facilityRatingsQueryKeys } from '../app/query-keys-factories/facility-ratings';
import { clearBrowserCookies } from '../utils/cookies';
import { generateZEnumFromEnum, setFormErrorsFromApiErrors } from '../utils/forms';
import { NotFound } from './NotFound';
import { handleApiError } from '../app/utils/errorhandling';

const validationSchema = z.object({
  general_rating: z.coerce
    .number()
    .min(1, { message: 'must be at least 1' })
    .max(5, { message: 'must be up to 5' }),
  specialty_support: z.coerce
    .number()
    .min(1, { message: 'must be at least 1' })
    .max(5, { message: 'must be up to 5' }),
  relationship_among_medical_staff_rating: z.coerce
    .number()
    .min(1, { message: 'must be at least 1' })
    .max(5, { message: 'must be up to 5' }),
  nursing_and_ancillary_support_rating: z.coerce
    .number()
    .min(1, { message: 'must be at least 1' })
    .max(5, { message: 'must be up to 5' }),
  culture_rating: z.coerce
    .number()
    .min(1, { message: 'must be at least 1' })
    .max(5, { message: 'must be up to 5' }),
  providers_group_culture_rating: z.coerce
    .number()
    .min(1, { message: 'must be at least 1' })
    .max(5, { message: 'must be up to 5' }),
  credentialing_rating: z.coerce
    .number()
    .min(1, { message: 'must be at least 1' })
    .max(5, { message: 'must be up to 5' }),
  onboarding_rating: z.coerce
    .number()
    .min(1, { message: 'must be at least 1' })
    .max(5, { message: 'must be up to 5' }),
  workload_rating: z.coerce
    .number()
    .min(1, { message: 'must be at least 1' })
    .max(5, { message: 'must be up to 5' }),
  resources_availability_rating: z.coerce
    .number()
    .min(1, { message: 'must be at least 1' })
    .max(5, { message: 'must be up to 5' }),
  comment: z.string().optional(),
});

export const FacilityRatingPage = () => {
  useDocumentTitle('Facility Rating');
  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const { facilityId } = useParams<{ facilityId: string }>();
  const navigate = useNavigate();
  const {
    handleSubmit,
    formState: { errors },
    register,
    clearErrors,
    control,
    setError,
    reset,
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  const {
    isLoading: isFacilityLoading,
    error: facilityFetchError,
    data: facility,
  } = useQuery({
    queryKey: healthcareEntityFacilityQueryKeys.detail(facilityId!),
    queryFn: async () =>
      await HealthcareEntityService.healthcareEntityFacilitiesRetrieve({
        id: facilityId!,
      }),
  });

  const {
    isLoading: isExistingResponseLoading,
    error: existingResponseError,
    data: existingResponsePage,
  } = useQuery({
    queryKey: facilityRatingsQueryKeys.list({
      facility: facilityId!,
      limit: 1,
      offset: 0,
    }),
    queryFn: async () =>
      await FacilityRatingsService.facilityRatingsList({
        facility: facilityId!,
        limit: 1,
        offset: 0,
      }),
  });

  const existingResponse = existingResponsePage?.results?.[0];

  useEffect(() => {
    if (existingResponse && !isExistingResponseLoading && !existingResponseError) {
      reset(existingResponse);
    }
  }, [existingResponse]);

  // Callback for clearing alerts received from server
  function handleClearErrorsAlert() {
    clearErrors();
  }

  const handleSubmitClick = () => {
    handleClearErrorsAlert();
  };

  const mutationPeerRating = useMutation({
    mutationFn: async (data: FacilityRating) => {
      await FacilityRatingsService.facilityRatingsCreate({ requestBody: data });
    },
  });

  // Callback for registration form submission
  const onSubmit = async (data) => {
    try {
      await mutationPeerRating.mutateAsync({
        ...data,
        facility: facilityId,
      } as FacilityRating);
      toast.success('Your rating has been submitted successfully!');
      navigate(`/app/organization/facilities/${facilityId}`);
    } catch (errors) {
      handleApiError(error);
      if (errors.status === 400) {
        setFormErrorsFromApiErrors(errors, setError);
      }
    }
  };

  if (!facility && !isFacilityLoading && facilityFetchError) {
    return <NotFound />;
  }

  return (
    facility && (
      <Container className="px-4 py-5" fluid>
        <Container>
          <Row className="d-flex justify-content-center align-items-center">
            <Col className="mb-5" lg={8} md={12}>
              {!isFacilityLoading && (
                <h1 className="fw-bold fs-1 text-uppercase text-center text-primary mt-5">
                  Rate facility {facility.name}
                </h1>
              )}
              <p>
                Please complete the Anonymous survey below regarding your experience at{' '}
                {facility.name}. Please elaborate in the comments as actionable feedback is a gift.
                Your answers will always remain confidential and never be shared in a public format.
              </p>
              <p>
                Your answers below are anonymous. Your personal information is only used to validate
                your identity as reviews must be completed by healthcare professionals.
              </p>
              <Row className="d-flex justify-content-center align-items-center mb-3">
                <Col lg={11} sm={11}>
                  <Form noValidate onSubmit={handleSubmit(onSubmit)} id="register-form">
                    <p>
                      <strong>
                        On a scale of 1 to 5, with 1 being poor and 5 being excellent, how would you
                        rate the facility above?
                      </strong>
                    </p>
                    <FormScaleSelectField
                      label={`Overall Rating of ${facility.name}`}
                      name="general_rating"
                      options={{
                        1: 'Poor',
                        2: 'Fair',
                        3: 'Good',
                        4: 'Very Good',
                        5: 'Excellent',
                      }}
                      errors={errors}
                      control={control}
                      required
                    />
                    <FormScaleSelectField
                      label="Specialty support:"
                      name="specialty_support"
                      options={{
                        1: 'Poor',
                        2: 'Fair',
                        3: 'Good',
                        4: 'Very Good',
                        5: 'Excellent',
                      }}
                      errors={errors}
                      control={control}
                      required
                    />
                    <FormScaleSelectField
                      label="Relationship among Medical Staff:"
                      name="relationship_among_medical_staff_rating"
                      options={{
                        1: 'Poor',
                        2: 'Fair',
                        3: 'Good',
                        4: 'Very Good',
                        5: 'Excellent',
                      }}
                      errors={errors}
                      control={control}
                      required
                    />
                    <FormScaleSelectField
                      label="Nursing and ancillary support:"
                      name="nursing_and_ancillary_support_rating"
                      options={{
                        1: 'Poor',
                        2: 'Fair',
                        3: 'Good',
                        4: 'Very Good',
                        5: 'Excellent',
                      }}
                      errors={errors}
                      control={control}
                      required
                    />
                    <FormScaleSelectField
                      label={`The culture at ${facility.name} is:`}
                      name="culture_rating"
                      options={{
                        1: 'Poor',
                        2: 'Fair',
                        3: 'Good',
                        4: 'Very Good',
                        5: 'Excellent',
                      }}
                      errors={errors}
                      control={control}
                      required
                    />
                    <FormScaleSelectField
                      label="The culture within the group of providers you worked with is:"
                      name="providers_group_culture_rating"
                      options={{
                        1: 'Poor',
                        2: 'Fair',
                        3: 'Good',
                        4: 'Very Good',
                        5: 'Excellent',
                      }}
                      errors={errors}
                      control={control}
                      required
                    />
                    <p>
                      <strong>
                        On a scale of 1 to 5, with 1 being Strongly Disagree and 5 being Strongly
                        Agree, how would you rate the facility above?
                      </strong>
                    </p>
                    <FormScaleSelectField
                      label={`The ${facility.name} Credentialing process was reasonable and clear.`}
                      name="credentialing_rating"
                      options={{
                        1: 'Strongly Disagree',
                        2: 'Disagree',
                        3: 'Neutral',
                        4: 'Agree',
                        5: 'Strongly Agree',
                      }}
                      errors={errors}
                      control={control}
                      required
                    />
                    <FormScaleSelectField
                      label="The Facility onboarding and orientation adequately prepared me to provide patient care."
                      name="onboarding_rating"
                      options={{
                        1: 'Strongly Disagree',
                        2: 'Disagree',
                        3: 'Neutral',
                        4: 'Agree',
                        5: 'Strongly Agree',
                      }}
                      errors={errors}
                      control={control}
                      required
                    />
                    <FormScaleSelectField
                      label={`The workload at ${facility.name} is reasonable and enables clinicians to provide quality care.`}
                      name="workload_rating"
                      options={{
                        1: 'Strongly Disagree',
                        2: 'Disagree',
                        3: 'Neutral',
                        4: 'Agree',
                        5: 'Strongly Agree',
                      }}
                      errors={errors}
                      control={control}
                      required
                    />
                    <FormScaleSelectField
                      label={`${facility.name} provides clinicians with the resources they need to do their job well.`}
                      name="resources_availability_rating"
                      options={{
                        1: 'Strongly Disagree',
                        2: 'Disagree',
                        3: 'Neutral',
                        4: 'Agree',
                        5: 'Strongly Agree',
                      }}
                      errors={errors}
                      control={control}
                      required
                    />
                    <FormTextareaField
                      label="Please provide any additional comments"
                      name="comment"
                      placeholder="Enter your comment"
                      errors={errors}
                      register={register}
                    />
                    <div className="d-grid py-3 mt-5">
                      <DevTool control={control} />
                      <pre>{JSON.stringify(errors)}</pre>
                      <Button
                        className="fs-4 py-3 rounded-0 btn-drop-shadow"
                        type="submit"
                        variant="primary"
                        onClick={() => handleSubmitClick()}
                        disabled={mutationPeerRating.isLoading || !isSubmitEnabled}
                      >
                        Submit
                      </Button>
                    </div>
                  </Form>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </Container>
    )
  );
};
